/** @format — POST /v1/agents/tracking/stream/{tracking_code} SSE client */

import { API_BASE_URL } from "../../config";
import { getToken } from "../../utils/tokenStorage";
import { parseStateDeltaOps } from "../chat-agui/parseStateDelta";
import { normalizeAguiComponents } from "../chat-agui/normalizeComponents";

const NOT_FOUND_REASONS = new Set([
  "shipment_not_found",
  "tracking_code_not_found",
  "TRACKING_CODE_NOT_FOUND",
]);

export class TrackingCodeNotFoundError extends Error {
  constructor(message = "Tracking code not found", { trackingCode, reason } = {}) {
    super(message);
    this.name = "TrackingCodeNotFoundError";
    this.code = "TRACKING_CODE_NOT_FOUND";
    this.trackingCode = trackingCode ?? null;
    this.reason = reason ?? "shipment_not_found";
  }
}

function authHeaders() {
  const token = getToken();
  const headers = {
    "Content-Type": "application/json",
    Accept: "text/event-stream",
  };
  if (token) {
    headers.Authorization = token.startsWith("Bearer ") ? token : `Bearer ${token}`;
  }
  return headers;
}

function isNotFoundPayload(payload) {
  if (!payload || typeof payload !== "object") return false;

  const detail = payload.detail ?? payload;
  const reason = detail.reason ?? payload.reason ?? payload.code;
  const message = String(detail.message ?? payload.message ?? "").toLowerCase();

  if (reason && NOT_FOUND_REASONS.has(reason)) return true;
  if (payload.code === "TRACKING_CODE_NOT_FOUND") return true;
  if (message.includes("not found") && (message.includes("tracking") || message.includes("shipment"))) {
    return true;
  }
  return false;
}

/** Parse FastAPI-style { detail: { message, trackingCode, reason } } from HTTP body */
export function parseTrackingHttpError(status, bodyText, trackingCode) {
  if (status !== 404 && status !== 403 && status !== 400) {
    return null;
  }

  let payload = null;
  try {
    payload = bodyText ? JSON.parse(bodyText) : null;
  } catch {
    if (bodyText && /not found/i.test(bodyText)) {
      return new TrackingCodeNotFoundError(bodyText, { trackingCode });
    }
    return null;
  }

  if (!payload) return null;

  if (isNotFoundPayload(payload)) {
    const detail = payload.detail ?? payload;
    return new TrackingCodeNotFoundError(
      detail.message ?? "Tracking code not found",
      {
        trackingCode: detail.trackingCode ?? trackingCode,
        reason: detail.reason ?? "shipment_not_found",
      },
    );
  }

  return null;
}

function eventIndicatesNotFound(event) {
  const code = event?.code ?? event?.data?.code;
  const reason = event?.reason ?? event?.data?.reason;
  const message = String(event?.message ?? event?.data?.message ?? event?.error ?? "").toLowerCase();

  if (code === "TRACKING_CODE_NOT_FOUND") return true;
  if (reason && NOT_FOUND_REASONS.has(reason)) return true;
  if (message.includes("tracking code not found") || message.includes("shipment not found")) {
    return true;
  }
  return false;
}

async function readSseStream(response, trackingCode, onEvent) {
  if (!response.ok) {
    const errText = await response.text().catch(() => "");
    const notFound = parseTrackingHttpError(response.status, errText, trackingCode);
    if (notFound) throw notFound;
    throw new Error(errText || `Tracking stream failed (${response.status})`);
  }

  const reader = response.body?.getReader();
  if (!reader) throw new Error("No response body for tracking stream");

  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split("\n");
    buffer = parts.pop() ?? "";

    for (const line of parts) {
      const trimmed = line.trim();
      if (!trimmed.startsWith("data:")) continue;
      const jsonStr = trimmed.slice(5).trim();
      if (!jsonStr || jsonStr === "[DONE]") continue;
      try {
        onEvent(JSON.parse(jsonStr));
      } catch {
        /* skip malformed */
      }
    }
  }
}

/**
 * @param {string} trackingCode
 * @param {object} payload — RunAgentInput shape
 * @param {object} handlers
 * @param {AbortSignal} [signal]
 */
export async function streamTrackingByCode(trackingCode, payload, handlers = {}, signal) {
  const code = String(trackingCode ?? "").trim();
  const encoded = encodeURIComponent(code);
  const url = `${API_BASE_URL}/v1/agents/tracking/stream/${encoded}`;

  const response = await fetch(url, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(payload),
    signal,
  });

  let text = "";
  let uiComponents = [];
  let threadId = payload.thread_id;
  let runId = payload.run_id;
  let streamError = null;
  let notFoundEvent = null;

  await readSseStream(response, code, (event) => {
    const type = event?.type;
    handlers.onRawEvent?.(event);

    switch (type) {
      case "RUN_STARTED":
        threadId = event.threadId ?? threadId;
        runId = event.runId ?? runId;
        text = "";
        uiComponents = [];
        handlers.onUiComponents?.([]);
        handlers.onRunStarted?.(event);
        break;

      case "STATE_SNAPSHOT": {
        const snap = event.snapshot ?? {};
        if (Array.isArray(snap.ui_components)) {
          uiComponents = normalizeAguiComponents(snap.ui_components);
          handlers.onUiComponents?.(uiComponents);
        }
        handlers.onStateSnapshot?.(snap);
        break;
      }

      case "TEXT_MESSAGE_START":
        handlers.onTextStart?.(event);
        break;

      case "TEXT_MESSAGE_CONTENT":
        if (typeof event.delta === "string") {
          text += event.delta;
          handlers.onTextDelta?.(text, event.delta);
        }
        break;

      case "TEXT_MESSAGE_END":
        handlers.onTextEnd?.(text);
        break;

      case "STATE_DELTA":
        if (Array.isArray(event.delta)) {
          const parsed = parseStateDeltaOps(event.delta, uiComponents);
          uiComponents = parsed.uiComponents;
          handlers.onUiComponents?.(uiComponents);
        }
        break;

      case "RUN_FINISHED":
        handlers.onRunFinished?.({ threadId, runId, text, uiComponents });
        break;

      case "RUN_ERROR":
        streamError = event.message ?? event.error ?? event.data?.message ?? "Tracking run failed";
        if (eventIndicatesNotFound(event)) {
          notFoundEvent = event;
        }
        handlers.onRunError?.(event);
        break;

      default:
        break;
    }
  });

  if (notFoundEvent || eventIndicatesNotFound({ message: streamError, code: streamError })) {
    throw new TrackingCodeNotFoundError(streamError ?? "Tracking code not found", {
      trackingCode: code,
      reason: notFoundEvent?.reason ?? notFoundEvent?.data?.reason ?? "shipment_not_found",
    });
  }

  if (streamError) {
    throw new Error(streamError);
  }

  return { threadId, runId, text, uiComponents };
}

/** Build a client-side not-found UI block for the chat panel */
export function buildTrackingNotFoundComponent(trackingCode, message) {
  return {
    id: `cmp_notfound_${Date.now()}`,
    name: "tracking.not_found",
    schemaVersion: 1,
    position: "append",
    kind: "primary",
    props: {
      trackingCode: String(trackingCode ?? "").replace(/^#/, ""),
      message:
        message ??
        "We couldn't find a shipment for this tracking number. Please check the number and try again.",
    },
  };
}
